import styled from 'styled-components';

import { Text } from '@/nova/components';
import { cardHover } from '@/nova/theme/configurator-keyframes';
import { CFG, CFG_FONT } from '../../../../../../theme/configurator-tokens';
import { FINISH } from '../../finish.tokens';

/* Natural size of the laptop + phone group; Stage scales it to fit. The box is
   sized to wrap the laptop with even side margins so it centres true (the laptop
   sits in the middle, the phone overlaps its lower-left corner). */
export const STAGE_W = 880;
export const STAGE_H = 540;

export const Root = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  /* v2 parity: headline + pill sit lower and inset from the edges (more central),
     not pinned to the top-left corner. Horizontal inset is a % so it tracks the
     viewport instead of crowding the title on narrower desktops. */
  padding: 150px 10% 0;
`;

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* Plain h1 (NOT styled(Text)) so its child <Text> nodes render — the project
   <Text> renders via dangerouslySetInnerHTML and discards React children. */
export const Title = styled.h1`
  font-family: ${CFG_FONT};
  font-size: 48px;
  font-weight: 300;
  line-height: 1;
  color: ${CFG.foreground};
`;

/* The accent word ("Casino") is a notch heavier than the light base (v2: 500 vs 300). */
export const TitleAccent = styled(Text)`
  font-family: ${CFG_FONT};
  font-weight: 500;
  color: ${CFG.brandText};
`;

export const Subtitle = styled(Text)`
  font-family: ${CFG_FONT};
  font-size: 16px;
  line-height: 1.15;
  color: ${CFG.base400};
`;

export const PreviewButton = styled.button`
  display: flex;
  flex-shrink: 0;
  cursor: pointer;
  align-items: center;
  gap: 8px;
  border-radius: 9999px;
  border: 1px solid ${FINISH.pillBorder};
  background: transparent;
  padding: 11px 20px;
  font-size: 15px;
  font-weight: 500;
  color: ${CFG.foreground};
  transition: background-color 150ms ease;

  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${cardHover}

  &:hover {
    background: ${FINISH.pillHover};
  }
`;

export const PreviewButtonLabel = styled(Text)`
  display: inline;
`;

/** Scales the device group to fill the available space, centred. */
export const Stage = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const StageGroup = styled.div<{ $scale: number }>`
  position: relative;
  width: ${STAGE_W}px;
  height: ${STAGE_H}px;
  transform: scale(${({ $scale }) => $scale});
`;

/** Sized to the laptop bezel's own aspect ratio, so either child (the static device,
 *  or the {@link MacAnchor} live-iframe fallback) fills a correctly-shaped box. */
export const MacSlot = styled.div`
  position: absolute;
  left: 55px;
  top: 21px;
  z-index: 0;
  width: 760px;
  aspect-ratio: 1113 / 723;
`;

/** Sized to the phone bezel's own aspect ratio; see {@link MacSlot}. */
export const PhoneSlot = styled.div`
  position: absolute;
  bottom: 17px;
  left: 85px;
  z-index: 10;
  height: 346px;
  aspect-ratio: 792 / 1619;
`;

/** Empty laptop-shaped anchor the preview host floats the warm desktop iframe over,
 *  rendered ONLY when the static screenshot is exhausted (both the active-language
 *  and `en` captures 404'd) — the live iframe as a last-resort fallback. */
export const MacAnchor = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

/** Empty phone-shaped anchor the preview host floats the warm mobile iframe over;
 *  see {@link MacAnchor}. */
export const PhoneAnchor = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const Loading = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

export const LoadingText = styled(Text)`
  font-size: 14px;
  color: ${CFG.base500};
`;
